<template>
	<card xs-tile sm-tile class="flex-grow-1">
		<test-mode-line v-if="eventStore.event.is_test"/>

		<card-text fill>
			<form-wizard v-if="participationClasses && participationClasses.length" v-model="activeStepIndex"
						 :action="$api('events.participations.store', {event: eventStore.event.id})"
						 :data="submitData" :steps="wizardSteps"
						 :color="eventStore.colorStyle"
						 persist-success-loading
						 v-model:valid="valid" v-model:loader="loading" @success="handleSuccessfulOrder" @error="handleError">

				<template v-for="(participationGroup, participationGroupIndex) in participationGroups"
						  :key="'group-' + participationGroupIndex" v-slot:[buildParticipationGroupWizardStepSlotName(participationGroupIndex)]>

					<participation-group-form-inputs :data="participationGroup" :participation-classes="participationClasses" :event="eventStore.event"
													 :creator-data="creatorData"
													 @removed-participation="index => handleRemovedParticipation(participationGroupIndex, index)"
													 :index="participationGroupIndex"
													 :validation-prefix="shouldSubmitAsGroup(participationGroup) ? `group.${participationGroupIndex}.` : ''"/>
				</template>

				<template v-slot:delivery>
					<delivery-data-form-inputs :data="deliveryData" :disabled="loading" required/>
				</template>

				<template v-slot:checkout>
					<row>
						<column v-if="totalPrice > 0 || showCreateAccountForm" cols="12" lg="7">
							<template v-if="showCreateAccountForm">
								<p class="subheading">{{ $t('action.create_free_account') }}</p>
								<p class="subheading-description">{{ $t('text.participation.create_free_account') }}</p>

								<form-switch v-model="createAccount" :label="$t('input.participation.create_account')"/>

								<alert v-if="hasVirtualParticipation && !createAccount" type="warning" small>{{
										$t('text.participation.virtual_participation_without_account')
									}}
								</alert>
							</template>
							<template v-if="totalPrice > 0">
								<p class="subheading">{{ $t('label.billing_address') }}</p>
								<p class="subheading-description">{{ $t('text.order.billing_address') }}</p>
								<billing-form-inputs :data="billingAddress" required :disabled="loading"/>
							</template>
						</column>
						<column cols="12" :lg="totalPrice > 0 || !$auth.loggedIn ? 5 : 12">
							<p class="subheading">{{ $t('label.summary') }}</p>
							<p class="subheading-description">{{ $t('text.order.summary') }} {{ isFree ? '' : $t('text.order.checkout_coupon_info') }}</p>

							<expansion-panels>
								<expansion-panel v-for="(orderStatement, orderStatementIndex) in orderStatements" :key="`summary-${orderStatementIndex}`">
									<template #header>
										<div class="flex justify-between w-full">
											<div class="flex flex-column">
                                            <span class="font-regular leading-spaced mb-2" :class="{'text-primary': !eventStore.colorStyle}"
												  :style="{color: eventStore.colorStyle}">
                                                {{ orderStatement.group }}
                                            </span>
												<span class="text-2xs leading-spaced">{{ orderStatement.title }}</span>
											</div>
											<div class="mx-8 text-success">
												{{ toEuro(orderStatement.amount, true, false, false) }}
											</div>
										</div>
									</template>

									<description-list v-if="orderStatement.details?.length" small>
										<description-list-item v-for="(detail, detailIndex) in orderStatement.details" :key="`summary-${orderStatementIndex}-${detailIndex}`"
															   :title="detail.title" :text="detail.type ? null : detail.text" :help="detail.help">
											<template v-if="detail.type" #text>
												<dynamic-input-value :input="detail" :value="detail.text"/>
											</template>
										</description-list-item>
									</description-list>
								</expansion-panel>
							</expansion-panels>

							<divider margin="16"/>

							<div class="flex justify-between items-center mt-24 mb-20">
								<div class="flex flex-column">
									<span class="text-md font-bold text-success">{{ $t('label.total_price') }}</span>
									<span class="text-4xs text-soft leading-tight">{{ $t('text.including_fees') }}</span>
								</div>
								<span class="text-xl font-bold text-success">{{ toEuro(totalPrice, true, false, false) }}</span>
							</div>
						</column>
					</row>
					<form-checkbox v-model="gtcAccepted" :rules="{accepted: true}" :label="childrenExists ? $t('input.participation.gtc_children') : $t('input.participation.gtc')">
						<div class="flex items-center">
							<a href="#" @click.prevent="globalStore.showGtcModal">{{ $t('action.open_app_gtc') }}</a>
							<span class="px-8">/</span>
							<a href="#" @click.prevent="showEventGtcModal = true">{{ $t('action.open_event_gtc') }}</a>
						</div>
					</form-checkbox>
					<client-only>
						<lazy-modal v-model="showEventGtcModal" :title="$t('label.event_gtc')">
							<server-content :action="$api('events.gtc', eventStore.event.id)"
											as-guest
											:data="{ts: eventStore.event.cache_buster}"
											:missing="$t('missing.event.gtc')"
											access="gtc" skeleton="paragraph@30"/>
						</lazy-modal>
					</client-only>
				</template>

				<template #buttons>
					<btn v-if="activeStepIndex !== wizardSteps.length" outlined :color="eventStore.colorStyle"
						 @click="addParticipationGroup()" large
						 :disabled="!valid">
						<icon :icon="icons.create"/>
						<span class="text-2xs sm:text-sm">{{ $t('action.add_participations') }}</span>
					</btn>
				</template>
			</form-wizard>
			<lazy-alert v-else :content="$t('text.event.no_participation_classes_for_participation')" type="warning" :button="$t('label.participation_classes')"
						:button-icon="icons.next" :to="$web('events.participation_classes.index', eventStore.event._key)" class="flex-grow-1" no-spacing/>
		</card-text>
	</card>
</template>

<script setup lang="ts">
import {deepEqual, getAnalyticsDataForEventProduct, getAnalyticsDataForParticipationClass, ParticipationInputState, toEuro} from "@spoferan/spoferan-ts-core";
import {createIcon, nextIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../../store/event";
import ParticipationRegistrationType from "../../../../models/ParticipationRegistrationType";
import type {Ref} from 'vue';
import {useGlobalStore} from "@spoferan/nuxt-spoferan/store";

const eventStore = useEventStore();
const globalStore = useGlobalStore();
const {t} = useI18n();
const {$api, $web, $date, $auth, $analytics} = useNuxtApp();
const route = useRoute();
const router = useRouter();

if (!eventStore.event.has_managed_sign_up || $date(eventStore.event.end_date).isBefore()) {
	await navigateTo($web('events.show', eventStore.event._key));
}

// The participation classes the user can select to participate in
const {data: participationClasses} = await useApiFetch($api('events.participations.create', eventStore.event._key))

const icons = {
	create: createIcon,
	next: nextIcon,
};

// The wizard step currently visible to the user
const activeStepIndex = ref(1);

// Whether the user wants to register an account with the data set on 'creatorData' with the event registration
const createAccount = ref(true);

// The data of the user who makes the registrations
const creatorData = ref({});

const gtcAccepted = ref(false);
const showEventGtcModal = ref(false);

// Whether the current wizard step is valid
const valid = ref(false);

// Whether the wizard form is currently being submitted
const loading = ref(false);

// Whether the user has input some data already
const didInputData = ref(false);
useLeavePageWarning(didInputData);

// An array of objects containing the participations to register
// Each object represents a group of participations managed within one wizard step
// [{title: string, subtitle: string, type: ParticipationRegistrationType, participations: []]
const participationGroups: Ref<{title: string, subtitle: string, type: ParticipationRegistrationType, participations: []}[]> = ref([{participations: [{}]}]);

const wizardSteps = computed(() => {
	const steps: {name: string, title: string, description?: string, next?: string, editable?: boolean}[] = [];
	participationGroups.value.forEach((group, groupIndex) => {
		steps.push({
			name: 'group_' + groupIndex,
			title: group.title ? group.title : t('label.participants'),
			subtitle: group.subtitle,
			editable: true
		})
	})

	if (requiresDeliveryData.value) {
		steps.push({
			name: 'delivery',
			title: t('label.delivery_address'),
			description: t('text.participation.delivery_address_required'),
			editable: true
		})
	}

	steps.push({
		name: 'checkout',
		title: t('label.checkout'),
		next: submitButtonText.value
	})
	return steps
});

// Whether the delivery data form shall be shown, i.e., a selected participation class or a selected event product requires a delivery address
const requiresDeliveryData = computed(() => {
	return participationGroups.value.some(group => {
		const participationClass = participationClasses && participationClasses.length ? participationClasses.find(participationClass => participationClass.id === group.participation_class_id) : null;

		return participationClass && (participationClass.settings.delivery_address_input !== ParticipationInputState.Disabled || group.participations && group.participations.length && group.participations.some(participation => {
			return participation.productPurchases && Object.keys(participation.productPurchases).length && Object.values(participation.productPurchases).some(purchase => purchase.product.requires_address)
		}));
	});
});

const childrenExists = computed(() => {
	return participationGroups.value.some(group => group.participations && group.participations.length && group.participations.some(participation => participation.birthday && $date(participation.birthday).age() < 18));
});

// Whether the show the form on which the registrant can create an account
// We only display this form if the user is not authenticated and does not already have an account
const showCreateAccountForm = computed(() => {
	return !$auth.loggedIn && !participationGroups.value.some(group => group.type === ParticipationRegistrationType.Myself && group.participations && group.participations.length && group.participations.some(participation => !!participation.user_id))
});

// The data to send to the server when submitting the registration form
const submitData = computed(() => {
	const data: {[key: string]: any} = {
		participations: [],
		groups: [],
		creator: creatorData.value,
		delivery: deliveryData.value,
		gtc: gtcAccepted.value
	}

	data.creator.create_account = showCreateAccountForm.value && createAccount.value

	if (totalPrice.value > 0) {
		data.billing = billingAddress.value
	}

	// Add the participations
	participationGroups.value.forEach((group, groupIndex) => {
		const groupParticipationSubmitData = [];
		group.participations.forEach((participation, participationIndex) => {
			const participationData = {
				statement_id: `participation_${groupIndex}_${participationIndex}`,
				expected_price_id: participation.activePrice ? participation.activePrice.id : undefined,
				participation_class_id: participation.participation_class_id ? participation.participation_class_id : undefined,
				user_id: participation.user_id ? participation.user_id : undefined,
				first_name: participation.first_name ? participation.first_name : undefined,
				last_name: participation.last_name ? participation.last_name : undefined,
				gender: participation.gender ? participation.gender : undefined,
				birthday: participation.birthday ? participation.birthday : undefined,
				email: participation.email ? participation.email : undefined,
				private: participation.private ? participation.private : false,
				participant_info: participation.participant_info,
				team_id: participation.team_id ? participation.team_id : undefined,
				club_id: participation.club_id,
				club_members_size: participation.club_members_size,
				club_presentation_type: participation.club_presentation_type,
				country_code: participation.country_code ? participation.country_code : undefined,
				state_code: participation.state_code ? participation.state_code : undefined,
				postcode: participation.postcode ? participation.postcode : undefined,
				city: participation.city ? participation.city : undefined,
				street: participation.street ? participation.street : undefined,
				phone: participation.phone ? participation.phone : undefined,
				license_number: participation.license_number ? participation.license_number : undefined,
				emergency_phone: participation.emergency_phone ? participation.emergency_phone : undefined,
				inputs: participation.inputs,
				products: {}
			}

			// Add the participation products to purchase
			if (participation.productPurchases && Object.keys(participation.productPurchases).length) {
				Object.values(participation.productPurchases).forEach((productPurchase, productPurchaseIndex) => {
					participationData.products[productPurchase.product_id] = {
						statement_id: `participation_${groupIndex}_${participationIndex}_purchase_${productPurchaseIndex}`,
						expected_price_id: productPurchase.activePrice ? productPurchase.activePrice.id : undefined,
						product_id: productPurchase.product_id,
						quantity: productPurchase.quantity,
						inputs: productPurchase.inputs
					}
				})
			}

			groupParticipationSubmitData.push(participationData)
		})

		if (shouldSubmitAsGroup(group)) {
			data.groups.push({
				team_id: group.team_id,
				participation_class_id: group.participation_class_id,
				statement_id: `group_${groupIndex}`,
				expected_price_id: group.activePrice ? group.activePrice.id : undefined,
				participations: groupParticipationSubmitData
			});
		} else {
			data.participations.push(...groupParticipationSubmitData);
		}
	})

	return data
});

// An array of objects including the amount and model type to pay
const orderStatements = computed(() => {
	const statements = [];
	participationGroups.value.forEach(group => {

		if (group.activePrice) {
			statements.push({
				group: `${t('label.participation')}: ${group.subtitle}`,
				title: group.title,
				amount: parseInt(group.activePrice.cents),
				details: group.participations.map((participation, participationIndex) => {
					return {
						title: `${t('label.participant')} ${participationIndex + 1}`,
						text: `${participation.first_name} ${participation.last_name}`
					}
				})
			});
		}

		// Iteration also required for groups to add the prices of the additional products
		group.participations.forEach(participation => {
			if (participation.activePrice) {
				const details: {title: string, text: string, help?: string}[] = [];

				if (participation._hide_gender || participation.gender) {
					details.push({
						title: t('label.gender'),
						text: participation._hide_gender ? t('label.provided') : (participation.gender === 'f' ? t('label.female') : t('label.male')),
						help: participation._hide_gender ? t('help.participation.data_from_account', {user: participation.first_name}) : null
					});
				}
				if (participation._hide_birthday || participation.birthday) {
					details.push({
						title: t('label.birthday'),
						text: participation._hide_birthday ? t('label.provided') : $date(participation.birthday).format('LL'),
						help: participation._hide_birthday ? t('help.participation.data_from_account', {user: participation.first_name}) : null
					});
				}
				if (participation.club_id) {
					details.push({
						title: t('label.club_company'),
						text: participation.club_name ?? t('label.provided'),
						help: !participation.club_name ? t('help.participation.data_from_account', {user: participation.first_name}) : null
					});
				}
				details.push({
					title: t('label.email'),
					text: participation.email ?? t('label.provided'),
					help: !participation.email ? t('help.participation.data_from_account', {user: participation.first_name}) : null
				});

				if (participation._hide_phone || participation.phone) {
					details.push({
						title: t('label.phone'),
						text: participation._hide_phone ? t('label.provided') : participation.phone,
						help: participation._hide_phone ? t('help.participation.data_from_account', {user: participation.first_name}) : null
					});
				}
				if (participation._hide_phone || participation.emergency_phone) {
					details.push({
						title: t('label.emergency_phone'),
						text: participation._hide_phone ? t('label.provided') : participation.emergency_phone,
						help: participation._hide_phone ? t('help.participation.data_from_account', {user: participation.first_name}) : null
					});
				}
				if (participation._hide_address || participation.country_code) {
					const addressParts = [participation.street, participation.city ? `${participation.postcode} ${participation.city}` : null, participation.country_code ? participation.country_code.toUpperCase() : null].filter(value => !!value);
					if (participation._hide_address || addressParts.length) {
						details.push({
							title: t('label.address'),
							text: participation._hide_address ? t('label.provided') : addressParts.join(', '),
							help: participation._hide_address ? t('help.participation.data_from_account', {user: participation.first_name}) : null
						});
					}
				}
				if (participation.inputs && Object.keys(participation.inputs).length) {
					details.push(...Object.keys(participation.inputs).map(inputId => {
						const inputData = participation.inputs[inputId];
						return {
							title: inputData.label,
							text: inputData.value,
							type: inputData.type
						}
					}));
				}

				if (participation.participant_info) {
					details.push({title: t('label.other'), text: participation.participant_info});
				}

				statements.push({
					group: `${t('label.participation')}: ${group.subtitle}`,
					title: `${participation.first_name} ${participation.last_name}`,
					amount: parseInt(participation.activePrice.cents),
					details: details
				});
			}

			// Add the prices for the selected products
			if (participation.productPurchases && Object.keys(participation.productPurchases).length) {
				Object.values(participation.productPurchases).forEach(productPurchase => {
					if (productPurchase.activePrice) {
						const details = [
							{title: t('label.quantity'), text: productPurchase.quantity}
						];

						// The purchase inputs are nested due to multi quantity
						if (productPurchase.inputs?.length) {
							productPurchase.inputs.forEach(quantityInputData => {
								details.push(...Object.keys(quantityInputData).map(inputId => {
									const inputData = quantityInputData[inputId];
									return {
										title: inputData.label,
										text: inputData.value
									}
								}));
							});
						}

						statements.push({
							group: productPurchase.product.name + (productPurchase.quantity > 1 ? ` (x${productPurchase.quantity})` : ''),
							title: `${participation.first_name} ${participation.last_name}`,
							amount: (productPurchase.quantity * productPurchase.activePrice.cents),
							details: details
						});
					}
				})
			}
		})
	})

	return statements
});

const {
	billingAddress,
	deliveryData,
	totalPrice,
	isFree,
	initializeDeliveryData,
	initializeBillingAddress
} = useCheckoutData(orderStatements)

// The text to insert on the final step's submit button
const submitButtonText = computed(() => {
	if (isFree.value) {
		return t('action.register_now')
	}

	return t('action.register_and_pay')
});

const hasVirtualParticipation = computed(() => {
	return participationGroups.value.some(group => group.participations && group.participations.length && group.participations.some(participation => participation.participationClass && participation.participationClass.is_virtual));
});

const analyticsItemListId = `event-participation-form-${eventStore.event.id}`;
const analyticsItemListName = `Event Participation Form: ${eventStore.event.display_name}`;

watch(activeStepIndex, (newValue, oldValue) => {
	// Prefill the delivery data with suggested data
	// We need to do this in here as only when switching to the delivery step all suggest data is available
	if (requiresDeliveryData.value && newValue === wizardSteps.value.length - 1) {
		initializeDeliveryData(getBillingAddressDefault())
	}

	// If we are on the last step, we initialize the checkout data based on the participation data
	if (newValue === wizardSteps.value.length) {
		initializeBillingAddress(getBillingAddressDefault());
	}
});

watch(submitData, (newValue, oldValue) => {
  if (!deepEqual(newValue, oldValue)) {
    didInputData.value = true;
  }
});

watch(requiresDeliveryData, (requiresDeliveryData) => {
	if (!requiresDeliveryData) {
		deliveryData.value = {};
	}
});

/**
 * The action to execute after the user successfully sent the participation form to the server.
 *
 * @param order
 */
function handleSuccessfulOrder(order) {
	didInputData.value = false; // Allows to leave the page without warnings

	const analyticsItems = getAnalyticsProductItems();
	$analytics.trackEvent('begin_checkout', {
		item_list_id: analyticsItemListId,
		item_list_name: analyticsItemListName,
		currency: 'EUR',
		value: (Math.round((totalPrice.value / 100) * 100) / 100),
		items: analyticsItems
	});

	// Trigger a register event when an account shall be created
	if (showCreateAccountForm.value && createAccount.value) {
		$analytics.trackEvent('sign_up', {method: 'participation'
		});
	}

	// Check if the user needs to pay the order
	if (order.requires_payment) {
		router.push($web('orders.pay', [order._key]))
	} else {
		router.push({
			path: $web('orders.show', [order._key]),
			query: {success_type: 'participation'}
		});
	}
}

/**
 * Adds a new participant to participate.
 */
function addParticipationGroup() {
	const participationGroup = {participations: [{}]};
	activeStepIndex.value = participationGroups.value.push(participationGroup)
}

function handleRemovedParticipation(participationGroupIndex, participationIndex) {
	// Remove the group if the last participation was removed
	if (participationGroupIndex > 0 && participationIndex === 0) {
		participationGroups.value.splice(participationGroupIndex, 1)
		activeStepIndex.value = Math.max(1, activeStepIndex.value - 1)
	}
}

/**
 * Gets the default billing address data when no user is authenticated.
 *
 * @returns {{}|{country_code: (*|null), city: (*|null), street: (*|null), name: string, postcode: (*|null), state_code: (*|null), email: (*|null)}}
 */
function getBillingAddressDefault() {
	if (!participationGroups.value.length) {
		return {};
	}

	let billingSuggestData: {[key: string]: any} = {}
	if (creatorData.value && Object.keys(creatorData.value).length) {
		billingSuggestData = {
			name: `${creatorData.value.first_name} ${creatorData.value.last_name}`,
			email: creatorData.value.email
		}
	}

	const creatorParticipation = participationGroups.value.map(group => group.participations).flat().find(participation => participation.first_name === creatorData.value.first_name && participation.last_name === creatorData.value.last_name)
	if (creatorParticipation) {
		billingSuggestData.country_code = billingSuggestData.country_code ? billingSuggestData.country_code : creatorParticipation.country_code;
		billingSuggestData.state_code = billingSuggestData.state_code ? billingSuggestData.state_code : creatorParticipation.state_code;
		billingSuggestData.postcode = billingSuggestData.postcode ? billingSuggestData.postcode : creatorParticipation.postcode;
		billingSuggestData.city = billingSuggestData.city ? billingSuggestData.city : creatorParticipation.city;
		billingSuggestData.street = billingSuggestData.street ? billingSuggestData.street : creatorParticipation.street;
	}

	return billingSuggestData
}

function handleError(status, response) {
	const errorCode = response.error_code ? response.error_code : 'unknown';
	switch (errorCode) {
		// Refresh the active price if the expected one is not valid anymore
		case 'unexpected_active_price':
			const statementId = response.data.statement_id;
			const activePrice = response.data.activePrice;
			participationGroups.value.forEach((group, groupIndex) => {
				const currentGroupStatementId = `group_${groupIndex}`;
				if (currentGroupStatementId === statementId) {
					group['activePrice'] = activePrice;
				} else {
					group.participations.forEach((participation, participationIndex) => {
						const currentParticipationStatementId = `participation_${groupIndex}_${participationIndex}`;
						if (currentParticipationStatementId === statementId) {
							participation['activePrice'] = activePrice;
						} else {
							if (participation.productPurchases && Object.keys(participation.productPurchases).length) {
								Object.values(participation.productPurchases).forEach((productPurchase, productPurchaseIndex) => {
									const currentPurchaseStatementId = `participation_${groupIndex}_${participationIndex}_purchase_${productPurchaseIndex}`;
									if (currentPurchaseStatementId === statementId) {
										productPurchase['activePrice'] = activePrice;
									}
								})
							}
						}
					})
				}
			})
			break;
	}
}

function getAnalyticsProductItems() {
	const products = [];
	participationGroups.value.forEach(group => {
		const participationClass = participationClasses && participationClasses.length ? participationClasses.find(participationClass => participationClass.id === group.participation_class_id) : null;

		if (participationClass && group.participations.length) {
			if (group.activePrice) {
				products.push(getAnalyticsDataForParticipationClass(eventStore.event, participationClass, 1, group.activePrice ? group.activePrice.cents : null))
			} else {
				products.push(getAnalyticsDataForParticipationClass(eventStore.event, participationClass, group.participations.length, group.participations[0].activePrice ? group.participations[0].activePrice.cents : null))
			}

			group.participations.forEach(participation => {
				if (participation.productPurchases && Object.keys(participation.productPurchases).length) {
					Object.values(participation.productPurchases).forEach(productPurchase => {
						products.push(getAnalyticsDataForEventProduct(eventStore.event, productPurchase.product, productPurchase.quantity, productPurchase.activePrice ? productPurchase.activePrice.cents : null));
					})
				}
			})
		}
	})

	return products;
}

function shouldSubmitAsGroup(group) {
	const participationClass = participationClasses?.length && group.participation_class_id ? participationClasses.find(participationClass => participationClass.id === group.participation_class_id) : null;

	return participationClass?.team_settings?.required;
}

function buildParticipationGroupWizardStepSlotName(index) {
	return 'group_' + index;
}

onMounted(() => {
	$analytics.trackEvent('view_item_list', {
		item_list_id: analyticsItemListId,
		item_list_name: analyticsItemListName,
		items: participationClasses.map((participationClass, participationClassIndex) => getAnalyticsDataForParticipationClass(eventStore.event, participationClass, 1, null, false, participationClassIndex))
	})
})

useMeta({
	title: eventStore.buildPageTitle(t('view.events.participations.create.title')),
	description: t('view.events.participations.create.description', {event: eventStore.event.display_name})
})

</script>

